import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import { List, MenuItem, Popover } from '@mui/material';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { clearPersonResponseStatusAction } from '@redux/person/personActions';
import {
  getPermissionRoleByIdSelector,
  getPermissionRoleTypeByIdSelector,
  getPersonByIdSelector,
  getPersonErrorStateSelector,
  getPersonLoadingStateSelector,
  getPersonPermissionRoleByIdSelector,
  getPersonPermissionRoleIdsByPersonIdSelector,
  getPersonSuccessStateSelector,
  hasClinicalAccountsCreatePermissionSelector,
  hasNonClinicalAccountsCreatePermissionSelector,
  hasOrgAdminAccountsCreatePermissionSelector,
  hasTeamMemberAccountsCreatePermissionSelector,
  isLoadingPostNewTeamMemberSelector
} from '@redux/person/personSelector';
import { getAllPermissionRolesThunk, getPersonPermissionsByOrgIdThunk, postNewTeamMemberThunk, putPersonPermissionRoleThunk, removePersonPermissionRoleThunk } from '@redux/person/personThunks';
import { NewTeamMemberRequest } from '@redux/person/personTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { newTeamMemberRoles } from 'config/newTeamMemberRoles';
import envelopeSimpleIcon from '../../../assets/images/envelope-simple.png';
import { AddCrossIcon, CancelCrossIcon } from 'components/Icons';
import { Button } from '../../../components/Button/Button';
import {
  CarePlanAutocomplete,
  CarePlanAutocompletePaper,
  CarePlanAutocompletePopper,
  CarePlanAutocompleteTextField as CarePlanTextField
} from '../../../components/CarePlanAutocomplete/CarePlanAutocomplete';
import { StatusMessage } from '../../../components/StatusMessage/StatusMessage';
import { TextField } from '../../../components/TextField';
import { Text } from '../../../components/Typography/Text';
import { emailAlertMessage, isEmailValid } from '../../../utils/util';
import ResendInviteButton from '../../AdminUserPermissions/ResendInviteButton';
import style from './Members.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & {};

const Members: React.FC<Props> = ({
  isLoadingPostNewTeamMember,
  activeOrganizationId,
  postNewTeamMember,
  hasTeamMemberAccountsCreatePermission,
  hasClinicalAccountsCreatePermission,
  hasNonClinicalAccountsCreatePermission,
  hasOrgAdminAccountsCreatePermission,
  getPersonPermissionsByOrgId,
  removePersonPermissionRole,
  putPersonPermissionRole,
  getAllPermissionRoles,
  personPermissionRoleIdsByPersonId,
  permissionRoleTypeById,
  permissionRoleById,
  personById,
  personPermissionRoleById,
  isLoading,
  error,
  success,
  clearResponseStatus
}) => {
  if (!hasTeamMemberAccountsCreatePermission) {
    return null;
  }

  const [personFormData, setPersonFormData] = useState({
    givenNames: '',
    familyName: '',
    email: '',
    permissionRoleType: {
      id: '',
      name: ''
    }
  });
  const [errors, setErrors] = useState({
    email: ''
  });
  const [addAnchorEl, setAddAnchorEl] = useState<Nullable<HTMLElement>[]>(Array(Object.values(permissionRoleById).length).fill(null));
  const [addOpen, setAddOpen] = useState<boolean[]>(Array(Object.values(permissionRoleById).length).fill(false));
  const { showAlertInfo, showAlertWarning } = useAlert();

  // Load members and permission roles on mount
  useEffect(() => {
    if (activeOrganizationId) {
      getPersonPermissionsByOrgId(activeOrganizationId);
    }
    getAllPermissionRoles();
  }, [activeOrganizationId, getPersonPermissionsByOrgId, getAllPermissionRoles]);

  const isSubmitBtnDisabled =
    isLoadingPostNewTeamMember || !personFormData.givenNames || !personFormData.familyName || !personFormData.email || !personFormData.permissionRoleType.id;

  const handlePersonChange = (e) => {
    const { name, value } = e.target;
    setPersonFormData({
      ...personFormData,
      [name]: value
    });
  };

  const handlePermissionTypeChange = (event, value) => {
    setPersonFormData({
      ...personFormData,
      permissionRoleType: value
    });
  };

  const validateForm = () => {
    const newErrors = {
      email: isEmailValid(personFormData.email) ? '' : emailAlertMessage(personFormData.email) || ''
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (validateForm() && personFormData.permissionRoleType.id) {
      const newTeamMember: NewTeamMemberRequest = {
        ...personFormData,
        permissionRoleTypeId: personFormData.permissionRoleType.id,
        organizationId: activeOrganizationId // Pass active org for super admins
      };
      try {
        await postNewTeamMember(newTeamMember);
        showAlertInfo({
          title: 'Success!',
          message: 'Team member added successfully.'
        });
        setPersonFormData({
          givenNames: '',
          familyName: '',
          email: '',
          permissionRoleType: {
            id: '',
            name: ''
          }
        });
        // Reload members list
        if (activeOrganizationId) {
          getPersonPermissionsByOrgId(activeOrganizationId);
        }
      } catch (err: any) {
        const status = err?.data?.status;
        const apiMessage = err?.data?.message;
        if (status === 409 && apiMessage) {
          showAlertWarning({
            title: 'Error',
            message: apiMessage
          });
        } else {
          showAlertWarning({
            title: 'Error',
            message: 'Something went wrong. Please try again.'
          });
        }
      }
    }
  };

  const handleAddButtonClick = (index: number, event: any) => {
    const addAnchorElCopy = [...addAnchorEl];
    addAnchorElCopy[index] = event.currentTarget;
    setAddAnchorEl(addAnchorElCopy);
    const addOpenCopy = [...addOpen];
    addOpenCopy[index] = true;
    setAddOpen(addOpenCopy);
  };

  const handleAddButtonClose = (index: number) => {
    const addAnchorElCopy = [...addAnchorEl];
    addAnchorElCopy[index] = null;
    setAddAnchorEl(addAnchorElCopy);
    const addOpenCopy = [...addOpen];
    addOpenCopy[index] = false;
    setAddOpen(addOpenCopy);
  };

  const onRemovePermissionRole = async (personPermissionRoleId: string, permissionRoleName: string) => {
    if (confirm(`Are you sure you want to remove this permission role: ${permissionRoleName}?`)) {
      await removePersonPermissionRole(personPermissionRoleId);
    }
  };

  const onAddPermissionRole = async (index: number, personId: string, permissionRoleId: string) => {
    await putPersonPermissionRole(personId, permissionRoleId);
    handleAddButtonClose(index);
  };

  const allowedTypes: { name: string; id?: string }[] = [...newTeamMemberRoles];

  return (
    <>
      <Text className={style.sectionTitle}>Add Member</Text>
      <form onSubmit={handleFormSubmit} className={style.mainContainer}>
        <div className={style.subContainer}>
          {/* TYPE */}
          <div>
            <Text variant="sm" className={style.labelSelect}>
              Member Type
            </Text>
            <div>
              <CarePlanAutocomplete
                options={allowedTypes}
                value={personFormData.permissionRoleType || null} // Ensure value is not undefined
                onChange={handlePermissionTypeChange}
                getOptionLabel={(option) => (option as any).name || ''}
                isOptionEqualToValue={(option, value) => (option as any).id === (value as any).id}
                PaperComponent={CarePlanAutocompletePaper}
                PopperComponent={CarePlanAutocompletePopper}
                renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                renderOption={(props, option, state) => (
                  <li {...props} key={(option as any).id}>
                    <div>{(option as any).name}</div>
                  </li>
                )}
              />
            </div>
          </div>
          {/* EMAIL */}
          <div>
            <TextField
              name="email"
              value={personFormData.email}
              onChange={handlePersonChange}
              label="Email"
              placeholder="Email"
              autoComplete="new-password"
              error={errors.email}
              leading={<img src={envelopeSimpleIcon} />}
            />
          </div>
          <div className={style.formRow}>
            {/* FIRST NAME */}
            <TextField name="givenNames" value={personFormData.givenNames} onChange={handlePersonChange} label="First name" placeholder="First name" autoComplete="new-password" />
            {/* LAST NAME */}
            <TextField name="familyName" value={personFormData.familyName} onChange={handlePersonChange} label="Last name" placeholder="Last name" autoComplete="new-password" />
          </div>
        </div>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitBtnDisabled} className={style.button}>
          Add new team member
        </Button>
      </form>

      {/* Members Table Section */}
      <div className={style.membersSection}>
        <Text className={style.membersHeader}>Members</Text>
        {activeOrganizationId && (
          <table className={style.table}>
            <thead>
              <tr>
                <th>
                  <Text variant="sm">Name</Text>
                </th>
                <th>
                  <Text variant="sm">Email</Text>
                </th>
                <th>
                  <Text variant="sm">Permissions</Text>
                </th>
                <th>
                  <Text variant="sm">Resend Invite</Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(personPermissionRoleIdsByPersonId).map((personId, personIdIndex) => {
                const person = personById[personId];
                const permissionRoleIds: string[] = [];
                if (person?.organizationId === activeOrganizationId) {
                  return (
                    <tr key={personId}>
                      <td>
                        <Text variant="sm">
                          {person.givenNames} {person.familyName}
                        </Text>
                      </td>
                      <td className={style.wrap}>
                        <Text variant="sm" className={style.textWrap}>
                          {person.email}
                        </Text>
                      </td>
                      <td>
                        {personPermissionRoleIdsByPersonId[personId]?.length === 0 && (
                          <button className={style.pillAdd} onClick={(event) => handleAddButtonClick(personIdIndex, event)}>
                            <AddCrossIcon width={20} height={20} />
                          </button>
                        )}
                        {personPermissionRoleIdsByPersonId[personId]?.map((personPermissionRoleId, index, array) => {
                          const isLast = index === array.length - 1;
                          const personPermissionRole = personPermissionRoleById[personPermissionRoleId];
                          if (!personPermissionRole) return null;

                          const permissionRole = permissionRoleById[personPermissionRole.permissionRoleId];
                          if (!permissionRole) return null;

                          const permissionRoleType = permissionRoleTypeById[permissionRole.permissionRoleTypeId];
                          if (!permissionRoleType?.name) return null;

                          permissionRoleIds.push(permissionRole.id);
                          const permissionRoleName = permissionRoleType.name;
                          const isSuperAdmin = permissionRoleName === 'Super Admin';
                          return (
                            <div key={personPermissionRoleId} className={classNames(style.pillContainer, isLast && style.last)}>
                              <div className={style.pill}>
                                <Text variant="sm">{permissionRoleName}</Text>
                                {!isSuperAdmin && (
                                  <button className={style.pillMinus} onClick={() => onRemovePermissionRole(personPermissionRoleId, permissionRoleName)}>
                                    <CancelCrossIcon width={20} height={20} />
                                  </button>
                                )}
                              </div>
                              {isLast && (
                                <button className={classNames(style.pillAdd, style.pillAddAbsolute)} onClick={(event) => handleAddButtonClick(personIdIndex, event)}>
                                  <AddCrossIcon width={20} height={20} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                        <Popover
                          anchorEl={addAnchorEl[personIdIndex]}
                          open={addOpen[personIdIndex] || false}
                          onClose={() => handleAddButtonClose(personIdIndex)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                          }}
                          className={style.popover}
                        >
                          <List className={style.contextMenu}>
                            {Object.keys(permissionRoleById).map((permissionRoleId) => {
                              const permissionRoleList = permissionRoleById[permissionRoleId];
                              if (!permissionRoleList) return null;
                              const permissionRoleTypeList = permissionRoleTypeById[permissionRoleList.permissionRoleTypeId];
                              if (!permissionRoleTypeList?.name) return null;
                              if (permissionRoleIds.includes(permissionRoleList.id)) return null;
                              // Prevent adding Super Admin role
                              if (permissionRoleTypeList.name === 'Super Admin') return null;
                              return (
                                <MenuItem key={permissionRoleId} onClick={() => onAddPermissionRole(personIdIndex, personId, permissionRoleId)}>
                                  <Text variant="md">{permissionRoleTypeList.name}</Text>
                                </MenuItem>
                              );
                            })}
                          </List>
                        </Popover>
                      </td>
                      <td>
                        <ResendInviteButton email={person.email} />
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
            </tbody>
          </table>
        )}
      </div>

      <StatusMessage
        saveSuccessful={
          (success &&
            success['person-redux/put-person-permission-role'] &&
            !isLoading['person-redux/put-person-permission-role'] &&
            !error['person-redux/put-person-permission-role']) ||
          (success &&
            success['person-redux/remove-person-permission-role'] &&
            !isLoading['person-redux/remove-person-permission-role'] &&
            !error['person-redux/remove-person-permission-role'])
        }
        saveUnsuccessful={
          (!isLoading['person-redux/put-person-permission-role'] && error && !!error['person-redux/put-person-permission-role']) ||
          (!isLoading['person-redux/remove-person-permission-role'] && error && !!error['person-redux/remove-person-permission-role'])
        }
        clearResponseStatus={clearResponseStatus}
        errorMessage={
          error && error['person-redux/put-person-permission-role']?.status === 401
            ? 'Unauthorized: You do not have permission to add this role.'
            : error && error['person-redux/remove-person-permission-role']?.status === 401
              ? 'Unauthorized: You do not have permission to remove this role.'
              : (error && (error['person-redux/put-person-permission-role']?.message || error['person-redux/remove-person-permission-role']?.message)) ||
                (error && error['person-redux/put-person-permission-role'] ? 'Error occurred while adding permission' : 'Error occurred while removing permission')
        }
      />
    </>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    isLoadingPostNewTeamMember: isLoadingPostNewTeamMemberSelector(state),
    hasTeamMemberAccountsCreatePermission: hasTeamMemberAccountsCreatePermissionSelector(state),
    hasClinicalAccountsCreatePermission: hasClinicalAccountsCreatePermissionSelector(state),
    hasNonClinicalAccountsCreatePermission: hasNonClinicalAccountsCreatePermissionSelector(state),
    hasOrgAdminAccountsCreatePermission: hasOrgAdminAccountsCreatePermissionSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state),
    personById: getPersonByIdSelector(state),
    personPermissionRoleIdsByPersonId: getPersonPermissionRoleIdsByPersonIdSelector(state),
    personPermissionRoleById: getPersonPermissionRoleByIdSelector(state),
    permissionRoleTypeById: getPermissionRoleTypeByIdSelector(state),
    permissionRoleById: getPermissionRoleByIdSelector(state),
    isLoading: getPersonLoadingStateSelector(state),
    error: getPersonErrorStateSelector(state),
    success: getPersonSuccessStateSelector(state)
  }),
  {
    postNewTeamMember: postNewTeamMemberThunk,
    getPersonPermissionsByOrgId: getPersonPermissionsByOrgIdThunk,
    removePersonPermissionRole: removePersonPermissionRoleThunk,
    putPersonPermissionRole: putPersonPermissionRoleThunk,
    getAllPermissionRoles: getAllPermissionRolesThunk,
    clearResponseStatus: () => clearPersonResponseStatusAction()
  }
);

export default compose(connectRedux)(Members);
